import React from "react";
import {
  Container,
  Typography,
  Button,
  Box,
  Grid,
  Paper,
  useTheme,
} from "@mui/material";
import ShieldIcon from "@mui/icons-material/Shield";
import SearchIcon from "@mui/icons-material/Search";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import GavelIcon from "@mui/icons-material/Gavel";
import PublicIcon from "@mui/icons-material/Public";
import PsychologyIcon from "@mui/icons-material/Psychology";
import BalanceIcon from "@mui/icons-material/Balance";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";

function About() {
  const theme = useTheme();
  return (
    <Container maxWidth="lg">
      {/* Hero Section */}
      <Box sx={{ pt: 8, pb: 6, textAlign: "center" }}>
        <Typography
          component="h1"
          variant="h2"
          color="primary"
          gutterBottom
          sx={{ fontWeight: "bold" }}
        >
          Welcome to Open Creed
        </Typography>
        <Typography
          variant="h4"
          color="text.secondary"
          paragraph
          sx={{ fontWeight: "medium" }}
        >
          An Honest Space to Investigate the Bible
        </Typography>
      </Box>
      {/* Mission Statement */}
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          p: 6,
          borderRadius: 2,
          mb: 6,
          textAlign: "center",
        }}
      >
        <Typography
          variant="h4"
          gutterBottom
          color="primary"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          🛡️ Mission
        </Typography>
        <Typography variant="body1" paragraph sx={{ fontSize: "1.2rem" }}>
          To present the foundational claims of belief and to create a truly
          open space where anyone, from any background, can explore the Bible
          honestly, critically, and deeply
        </Typography>
        <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem" }}>
          We gather the evidence. We lay out the reasoning. We invite you to
          test every assertion so you can decide what stands, and what
          collapses.
        </Typography>
        <Typography variant="body1" sx={{ fontStyle: "italic", mt: 3 }}>
          No verdict is handed down.
          <br />
          The judgment belongs to you.
        </Typography>
      </Box>
      {/* Key Features */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <ShieldIcon sx={{ fontSize: 60, color: "primary.main", mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Intellectual Integrity
            </Typography>
            <Typography>
              Every claim is treated with fairness, scrutiny, and full
              transparency. No assumptions hidden, no facts cherry-picked.
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <SearchIcon sx={{ fontSize: 60, color: "primary.main", mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Radical Openness
            </Typography>
            <Typography>
              All sources, all arguments, and all objections are welcome —
              because if a creed is true, it can survive the light.
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <LightbulbIcon
              sx={{ fontSize: 60, color: "primary.main", mb: 2 }}
            />
            <Typography variant="h5" gutterBottom>
              Respect for the Mind
            </Typography>
            <Typography>
              We trust our readers. We do not manipulate emotions or bypass
              reason. We assume you want the truth — and you're willing to do
              the work.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
      {/* Core Values */}
      <Typography
        component="h2"
        variant="h4"
        color="primary"
        gutterBottom
        align="center"
        sx={{ fontWeight: "bold" }}
      >
        📜 Core Values
      </Typography>
      <Grid container spacing={4} sx={{ mt: 2 }}>
        {/* Intellectual Integrity */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={2}
            sx={{
              p: 4,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <GavelIcon sx={{ fontSize: 60, color: "primary.main", mb: 2 }} />
            <Typography variant="h5" gutterBottom color="primary">
              Intellectual Integrity
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "1.1rem" }}>
              We do not hide assumptions, cherry-pick facts, or push
              conclusions. Every claim is treated with fairness, scrutiny, and
              full transparency.
            </Typography>
          </Paper>
        </Grid>
        {/* Radical Openness */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={2}
            sx={{
              p: 4,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <PublicIcon sx={{ fontSize: 60, color: "primary.main", mb: 2 }} />
            <Typography variant="h5" gutterBottom color="primary">
              Radical Openness
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "1.1rem" }}>
              We believe that truth does not fear investigation; it invites it,
              because what is truly eternal can withstand the weight of every
              honest question. Our platform encourages critical thinking and
              honest questioning, not blind belief.
            </Typography>
          </Paper>
        </Grid>
        {/* Respect for the Mind */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={2}
            sx={{
              p: 4,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <PsychologyIcon
              sx={{ fontSize: 60, color: "primary.main", mb: 2 }}
            />
            <Typography variant="h5" gutterBottom color="primary">
              Respect for the Mind
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "1.1rem" }}>
              We trust our readers. We assume you want the truth — and you're
              willing to do the work.
            </Typography>
          </Paper>
        </Grid>
        {/* Serious Stakes */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={2}
            sx={{
              p: 4,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <BalanceIcon sx={{ fontSize: 60, color: "primary.main", mb: 2 }} />
            <Typography variant="h5" gutterBottom color="primary">
              Serious Stakes
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "1.1rem" }}>
              The claims we examine are not purely academic. They are
              existential. They concern meaning, morality, eternity, identity,
              and the nature of reality. We treat them with the weight they
              deserve.
            </Typography>
          </Paper>
        </Grid>
        {/* Open Inquiry */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={2}
            sx={{
              p: 4,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <SearchIcon sx={{ fontSize: 60, color: "primary.main", mb: 2 }} />
            <Typography variant="h5" gutterBottom color="primary">
              Open Inquiry
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "1.1rem" }}>
              We believe truth does not fear questions. Every verse, every
              claim, and every story in the Bible is open for exploration,
              backed by historical, archaeological, and scientific research.
            </Typography>
          </Paper>
        </Grid>
        {/* Radical Transparency */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={2}
            sx={{
              p: 4,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <AutoStoriesIcon
              sx={{ fontSize: 60, color: "primary.main", mb: 2 }}
            />
            <Typography variant="h5" gutterBottom color="primary">
              Radical Transparency
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "1.1rem" }}>
              We are making the Bible open-source—literally. With every passage,
              users can click to access AI-powered context, diverse
              interpretations, and the evidence behind it. See how different
              worldviews approach the same scripture.
            </Typography>
          </Paper>
        </Grid>
        {/* Dialogue Over Imposed Faith */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={2}
            sx={{
              p: 4,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <QuestionAnswerIcon
              sx={{ fontSize: 60, color: "primary.main", mb: 2 }}
            />
            <Typography variant="h5" gutterBottom color="primary">
              Dialogue Over Imposed Faith
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "1.1rem" }}>
              We don't preach—we debate. Engage with an AI trained in various
              religious and philosophical perspectives. Explore how Jews,
              Christians, Muslims, atheists, and others might interpret
              theology.
            </Typography>
          </Paper>
        </Grid>
        {/* Wonder Over Certainty */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={2}
            sx={{
              p: 4,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <TipsAndUpdatesIcon
              sx={{ fontSize: 60, color: "primary.main", mb: 2 }}
            />
            <Typography variant="h5" gutterBottom color="primary">
              Wonder Over Certainty
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "1.1rem" }}>
              We're not here to give you all the answers—we're here to help you
              ask better questions.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}

export default About;
